import React, { useEffect, useMemo, useRef } from 'react';
import { Box, CardTypesEnum, Preloader } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import { Access } from '@/modules/core/components';
import { useCurrentOrganization, useHistory } from '@/modules/core/hooks';
import theme from '@/styles/theme';

import { PlanStatuses } from '../plan.types';
import usePlanTerminationBanner from '../PlanTermination/hooks/usePlanTerminationBanner';

import useEditMode from './hooks/useEditMode';
import AccountFunding from './AccountFunding';
import ActivityLogSection from './ActivityLogSection';
import Coverage from './Coverage';
import DangerZone from './DangerZone';
import EligibleServicesSection from './EligibleServicesSection';
import FinalSetup from './FinalSetup';
import { useCurrentPlan, usePlanSetupSetter } from './hooks';
import PlanDefinition from './PlanDefinition';
import PlanSetupPrompt from './PlanSetupPrompt';
import PlanSetupWrapper from './PlanSetupWrapper';
import PlanTopbar from './PlanTopbar';
import { usePlanLoadingStore, usePreviewStore, useSubmitFormStore } from './stores';

const StyledBox = styled(Box)`
  flex: 1 0;
`;
// const TIMEOUT = 1000;
interface Props {
  defaultEditMode?: boolean;
  isTemplateMode?: boolean;
}
const Plans: React.FC<Props> = ({ defaultEditMode, isTemplateMode }) => {
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const activateButtonRef = useRef<HTMLButtonElement>(null);
  const reviewButtonRef = useRef<HTMLButtonElement>(null);
  const {
    observingOrganization: { type },
  } = useCurrentOrganization();
  const { planStatus, planName, planId, planType, submitClaimsDateEnd } = useCurrentPlan();
  const isPlanActive = useMemo(
    () => planStatus === PlanStatuses.ACTIVE || planStatus === PlanStatuses.PENDING_ACTIVE,
    [planStatus]
  );
  const isRunoutPassed = useMemo(() => {
    if (!isPlanActive || !submitClaimsDateEnd) return false;
    return dayjs(submitClaimsDateEnd).isBefore(dayjs(), 'day');
  }, [isPlanActive, submitClaimsDateEnd]);
  const isTerminatedOrInProgress = useMemo(
    () =>
      planStatus === PlanStatuses.TERMINATED ||
      planStatus === PlanStatuses.TERMINATION_IN_PROGRESS ||
      planStatus === PlanStatuses.PENDING_TERMINATION,
    [planStatus]
  );
  const previewMode = usePreviewStore((state) => state.previewMode);
  const { handleSetPlanTerminationDetails } = usePlanTerminationBanner();

  const loading = usePlanLoadingStore((state) => state.loading);
  const isEditMode = useEditMode(defaultEditMode, isPlanActive, isTemplateMode);
  const isSubsidiary = useMemo(() => type === OrganizationTypes.subsidiary, [type]);
  const isEditModeApplied = useMemo(
    () => isEditMode && !previewMode && !isSubsidiary,
    [isEditMode, previewMode, isSubsidiary]
  );

  const setPlanLoadedId = usePlanLoadingStore((state) => state.setPlanLoadedId);
  useEffect(() => {
    return () => {
      if (setPlanLoadedId) setPlanLoadedId('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { handleReset } = usePlanSetupSetter();
  const handleResetSubmit = useSubmitFormStore((state) => state.reset);
  useEffect(
    () => () => {
      handleResetSubmit();
      handleReset();
    },
    [handleReset, handleResetSubmit]
  );
  const history = useHistory();
  useEffect(() => {
    if (planStatus === PlanStatuses.DELETED) {
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planStatus]);

  useEffect(() => {
    if (planId) {
      handleSetPlanTerminationDetails(`${planId}`, planName, planType === CardTypesEnum.HSA, planStatus);
    }
  }, [handleSetPlanTerminationDetails, planId, planName, planType, planStatus]);

  if (loading) {
    return <Preloader testId="preloader" />;
  }

  return (
    <PlanSetupWrapper planStatus={planStatus} isTemplateMode={isTemplateMode} isPlanActive={isPlanActive}>
      <PlanSetupPrompt isTemplateMode={isTemplateMode} />
      <PlanTopbar
        saveButtonRef={saveButtonRef}
        activateButtonRef={activateButtonRef}
        reviewButtonRef={reviewButtonRef}
        viewMode={!isEditMode || isSubsidiary}
        planStatus={planStatus}
        planName={planName}
        isTemplateMode={isTemplateMode}
        isPlanActive={isPlanActive}
      />
      <StyledBox width={theme.defaultContentWidth}>
        <PlanDefinition
          isEditMode={isEditModeApplied}
          isTemplateMode={isTemplateMode}
          isPlanActive={isPlanActive}
          isTerminated={isTerminatedOrInProgress}
        />
        <Coverage
          isTemplateMode={isTemplateMode}
          isEditMode={isEditModeApplied}
          isRunoutPassed={isRunoutPassed}
        />
        <AccountFunding
          isEditMode={isEditModeApplied}
          isTemplateMode={isTemplateMode}
          isTerminated={isTerminatedOrInProgress}
        />
        <EligibleServicesSection
          isEditMode={isEditModeApplied}
          isTemplateMode={isTemplateMode}
          isPlanActive={isPlanActive}
        />
        <ActivityLogSection />
        {!previewMode && !isSubsidiary && (
          <FinalSetup
            saveButtonRef={saveButtonRef}
            activateButtonRef={activateButtonRef}
            reviewButtonRef={reviewButtonRef}
            isTemplateMode={isTemplateMode}
            isEditMode={isEditModeApplied}
            isPlanActive={isPlanActive}
            planName={planName}
            isPlanTerminated={isTerminatedOrInProgress}
          />
        )}
        {!previewMode && !isSubsidiary && (
          <Access
            accessRequirements={[
              {
                permission: isTemplateMode ? Permissions.PLAN_TEMPLATE_SETUP : Permissions.PLAN_SETUP,
              },
            ]}
          >
            <DangerZone viewMode={isEditModeApplied} isTemplateMode={isTemplateMode} />
          </Access>
        )}
      </StyledBox>
    </PlanSetupWrapper>
  );
};
export default Plans;
